import { ref, onUnmounted } from 'vue';
import { useAuthStore } from '../stores/auth';
import type { Build } from '@kilnci/shared';

import type { StageResult } from '@kilnci/shared';

interface BuildsEventMessage {
  type: 'connected' | 'build:created' | 'build:updated' | 'build:stages-updated' | 'ping';
  payload?: unknown;
}

interface UseBuildsEventsOptions {
  onBuildCreated?: (build: Build) => void;
  /**
   * Fired on every build-row change master broadcasts via NOTIFY.
   * Payload is a full Build sans `stages` (stages have their own
   * channel), so the UI has everything it needs — workerId,
   * workerName, status, finishedAt, exitCode, etc. — to patch the
   * list and currentBuild without a refetch.
   */
  onBuildUpdated?: (build: Omit<Build, 'stages'>) => void;
  onBuildStagesUpdated?: (id: string, stages: StageResult[]) => void;
}

export function useBuildsEvents(options: UseBuildsEventsOptions = {}) {
  const authStore = useAuthStore();
  const connected = ref(false);
  const error = ref<string | null>(null);

  let eventSource: EventSource | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectAttempts = 0;
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000;

  function connect() {
    if (!authStore.isAuthenticated) {
      error.value = 'Authentication required';
      return;
    }

    if (eventSource) {
      disconnect();
    }

    // Session cookie is sent automatically with EventSource request
    const url = '/api/builds/events';
    eventSource = new EventSource(url);

    eventSource.onopen = () => {
      connected.value = true;
      error.value = null;
      reconnectAttempts = 0;
    };

    eventSource.onmessage = (event) => {
      try {
        const message: BuildsEventMessage = JSON.parse(event.data);

        switch (message.type) {
          case 'connected':
            // Connection confirmed
            break;
          case 'build:created': {
            const build = message.payload as Build;
            options.onBuildCreated?.(build);
            break;
          }
          case 'build:updated': {
            const build = message.payload as Omit<Build, 'stages'>;
            options.onBuildUpdated?.(build);
            break;
          }
          case 'build:stages-updated': {
            const payload = message.payload as { id: string; stages: StageResult[] };
            options.onBuildStagesUpdated?.(payload.id, payload.stages);
            break;
          }
          case 'ping':
            // Keepalive ping, ignore
            break;
        }
      } catch {
        // Ignore invalid messages
      }
    };

    eventSource.onerror = () => {
      connected.value = false;

      // EventSource automatically tries to reconnect, but we want to handle
      // auth errors and limit reconnection attempts
      if (eventSource?.readyState === EventSource.CLOSED) {
        eventSource = null;
        scheduleReconnect();
      }
    };
  }

  function scheduleReconnect() {
    if (reconnectAttempts >= maxReconnectAttempts) {
      error.value = 'Failed to connect after multiple attempts';
      return;
    }

    // Exponential backoff
    const delay = baseReconnectDelay * Math.pow(2, reconnectAttempts);
    reconnectAttempts++;

    reconnectTimer = setTimeout(() => {
      connect();
    }, delay);
  }

  function disconnect() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
    connected.value = false;
    reconnectAttempts = 0;
  }

  // Cleanup on unmount
  onUnmounted(() => {
    disconnect();
  });

  return {
    connected,
    error,
    connect,
    disconnect,
  };
}
